import React, { useState } from 'react';
import { userAPI } from '../lib/api';

/**
 * Self-service password change (uses PUT /api/users/me/password).
 */
const ChangePasswordModal = ({ isOpen, onClose, onSuccess }) => {
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');

	if (!isOpen) return null;

	const resetForm = () => {
		setCurrentPassword('');
		setNewPassword('');
		setConfirmPassword('');
		setError('');
	};

	const handleClose = () => {
		resetForm();
		onClose?.();
	};

	const handleSubmit = async (e) => {
		e?.preventDefault();
		setError('');
		if (newPassword !== confirmPassword) {
			setError('New passwords do not match');
			return;
		}
		if (newPassword.length < 8) {
			setError('New password must be at least 8 characters');
			return;
		}
		setSubmitting(true);
		try {
			await userAPI.changeMyPassword(currentPassword, newPassword);
			resetForm();
			onSuccess?.();
			onClose?.();
		} catch (err) {
			const msg =
				err.response?.data?.message ||
				err.response?.data?.error ||
				err.message ||
				'Failed to change password';
			setError(msg);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
			<div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl border border-slate-200">
				<div className="flex justify-between items-center mb-4">
					<h3 className="text-lg font-semibold text-slate-900">Change password</h3>
					<button
						type="button"
						onClick={handleClose}
						className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"
						aria-label="Close"
					>
						<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
				<form onSubmit={handleSubmit} className="space-y-4">
					{error && (
						<div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
					)}
					<div>
						<label className="block text-sm font-medium text-slate-700 mb-1">Current password</label>
						<input
							type="password"
							autoComplete="current-password"
							value={currentPassword}
							onChange={(e) => setCurrentPassword(e.target.value)}
							className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
						<input
							type="password"
							autoComplete="new-password"
							value={newPassword}
							onChange={(e) => setNewPassword(e.target.value)}
							className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-slate-700 mb-1">Confirm new password</label>
						<input
							type="password"
							autoComplete="new-password"
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
							className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
						/>
					</div>
					<div className="flex justify-end gap-2 pt-2">
						<button
							type="button"
							onClick={handleClose}
							className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={submitting}
							className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
						>
							{submitting ? 'Saving…' : 'Update password'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default ChangePasswordModal;
